import { useState, useMemo } from "react";

export const TYPE_META = {
  meeting_first:  { label: "פגישה ראשונית",          icon: "🤝", color: "#059669" },
  meeting:        { label: "פגישת ייעוץ",             icon: "📋", color: "#0891b2" },
  call:           { label: "שיחת טלפון",              icon: "📞", color: "#475569" },
  correspondence: { label: "תכתובת (מייל/וואטסאפ)",  icon: "💬", color: "#475569" },
  report_sent:    { label: "שליחת דוח ללקוח",         icon: "📤", color: "#7c3aed" },
  fee:            { label: "עלות ייעוץ",              icon: "💰", color: "#d97706" },
  task:           { label: "משימה / תזכורת",           icon: "🔔", color: "#dc2626" },
  signature:      { label: "בקשת חתימה",               icon: "✍️", color: "#2563eb" },
};

const TODAY = () => new Date().toISOString().slice(0, 10);

function dateDiffDays(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.floor((d - now) / 86400000);
}

function fmtDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("he-IL", {
    day: "2-digit", month: "2-digit", year: "2-digit",
  });
}
function SignatureTracker({ entry }) {
  const SERVER_URL = process.env.REACT_APP_SERVER_URL || "";
  const [status, setStatus] = useState(entry.status || "pending");
  const [checking, setChecking] = useState(false);

  const checkStatus = async () => {
    setChecking(true);
    try {
      const r = await fetch(`${SERVER_URL}/api/sign/status/${entry.token}`);
      const { signed } = await r.json();
      if (signed) setStatus("signed");
      else alert("עדיין לא נחתם.");
    } catch (e) {
      alert("שגיאה בבדיקת סטטוס.");
    }
    setChecking(false);
  };

  const downloadSigned = () => {
    const a = document.createElement("a");
    a.href = `${SERVER_URL}/api/sign/download/${entry.token}`;
    a.download = `חתום_${entry.clientName || "לקוח"}.pdf`;
    a.click();
  };

  if (status === "signed") {
    return (
      <button onClick={downloadSigned} style={{
        marginTop: 6, padding: "4px 10px", background: "#f0fdf4", color: "#166534",
        border: "1px solid #86efac", borderRadius: 6, fontSize: 11, cursor: "pointer", fontWeight: 700
      }}>⬇️ הורד טופס חתום</button>
    );
  }

  return (
    <div style={{ marginTop: 6, display: "flex", gap: 6, alignItems: "center" }}>
      <button onClick={checkStatus} disabled={checking} style={{
        padding: "4px 10px", background: "#f8fafc", color: "#475569",
        border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 11, cursor: "pointer"
      }}>
        {checking ? "בודק..." : "🔄 בדוק סטטוס"}
      </button>
      <span style={{ fontSize: 11, color: "#94a3b8" }}>{entry.formName} (ממתין)</span>
    </div>
  );
}

export default function ActivityLogPanel({ entries, onAdd, onUpdate, onDelete, clients = [], onClose, defaultClient = "הכל" }) {
  const [filterType, setFilterType] = useState("הכל");
  const [filterClient, setFilterClient] = useState(defaultClient === "הכל" ? "" : defaultClient);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const [form, setForm] = useState({
    type: "meeting",
    description: "",
    clientName: "",
    amount: "",
    feeCollected: false,
    date: TODAY(),
    dueDate: "",
    reminder: "",
  });

  // ── Open tasks ──────────────────────────────────────────────
  const openTasks = useMemo(() =>
    entries
      .filter(e => e.type === "task" && !e.done)
      .sort((a, b) => (a.dueDate || "9999") < (b.dueDate || "9999") ? -1 : 1),
    [entries]
  );

  // ── Client names for dropdowns ───────────────────────────────
  const clientOptions = useMemo(() => {
    const fromClients = clients.map(c => c.fileInfo?.client).filter(Boolean);
    const fromEntries = entries.map(e => e.clientName).filter(Boolean);
    return [...new Set([...fromClients, ...fromEntries])].sort((a, b) => a.localeCompare(b, "he"));
  }, [clients, entries]);

  // ── Filtered timeline ────────────────────────────────────────
  const clientQuery = filterClient.trim();
  const filtered = useMemo(() => {
    return entries.filter(e => {
      if (e.type === "note") return false; // Hide notes from the global activity log
      if (filterType === "פתוחות") return e.type === "task" && !e.done;
      if (filterType !== "הכל" && e.type !== filterType) return false;
      if (clientQuery && !(e.clientName || "").includes(clientQuery)) return false;
      return true;
    });
  }, [entries, filterType, clientQuery]);

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!form.description.trim()) return;
    onAdd({
      type: form.type,
      description: form.description.trim(),
      clientName: form.clientName.trim() || undefined,
      ...(form.type === "fee" ? {
        amount: Number(form.amount) || undefined,
        feeCollected: form.feeCollected,
      } : {}),
      ...(form.type === "task" ? {
        dueDate: form.dueDate || undefined,
        done: false,
      } : {}),
      ...(form.type === "note" && form.reminder ? { reminder: form.reminder } : {}),
      date: form.date || TODAY(),
    });
    setForm({ type: "meeting", description: "", clientName: "", amount: "", feeCollected: false, date: TODAY(), dueDate: "", reminder: "" });
    setShowForm(false);
  };

  // ── Inline edit ──────────────────────────────────────────────
  const startEdit = (entry) => {
    setEditId(entry.id);
    setEditForm({
      description: entry.description || "",
      clientName: entry.clientName || "",
      date: entry.date || TODAY(),
      dueDate: entry.dueDate || "",
      amount: entry.amount ?? "",
      reminder: entry.reminder || "",
    });
  };

  const saveEdit = (entry) => {
    if (!editForm.description.trim()) return;
    onUpdate(entry.id, {
      description: editForm.description.trim(),
      clientName: editForm.clientName.trim() || undefined,
      date: editForm.date || entry.date,
      ...(entry.type === "task" ? { dueDate: editForm.dueDate || undefined } : {}),
      ...(entry.type === "fee" ? { amount: Number(editForm.amount) || undefined } : {}),
      ...(entry.type === "note" ? { reminder: editForm.reminder || undefined } : {}),
    });
    setEditId(null);
    setEditForm(null);
  };

  const cancelEdit = () => { setEditId(null); setEditForm(null); };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 3000,
      background: "rgba(15,23,42,0.45)",
      display: "flex", alignItems: "flex-start", justifyContent: "flex-end",
      direction: "rtl",
    }}>
      <div style={{
        width: 520, maxWidth: "100vw", height: "100vh",
        background: "#f8fafc",
        display: "flex", flexDirection: "column",
        boxShadow: "-4px 0 24px rgba(0,0,0,0.15)",
      }}>

        {/* ── Header ── */}
        <div style={{
          padding: "14px 20px", background: "white",
          borderBottom: "1px solid #e2e8f0",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexShrink: 0,
        }}>
          <button onClick={onClose} style={iconBtn}>✕</button>
          <span style={{ fontSize: 16, fontWeight: 800, color: "#1e293b" }}>📋 יומן פעולות</span>
          <div style={{ width: 28 }} />
        </div>

        {/* ── Open tasks section ── */}
        {openTasks.length > 0 && (
          <div style={{
            margin: "12px 16px 0", background: "white",
            borderRadius: 10, border: "1px solid #fecaca",
            overflow: "hidden", flexShrink: 0,
          }}>
            <div style={{
              padding: "8px 14px", background: "#fff7f7",
              borderBottom: "1px solid #fecaca",
              fontSize: 12, fontWeight: 700, color: "#dc2626",
              display: "flex", alignItems: "center", gap: 6,
            }}>
              🔔 משימות פתוחות ({openTasks.length})
            </div>
            {openTasks.map(t => {
              const diff = dateDiffDays(t.dueDate);
              const overdue = diff !== null && diff < 0;
              const urgent  = diff !== null && diff <= 1 && diff >= 0;
              const rowColor = overdue ? "#fef2f2" : urgent ? "#fffbeb" : "white";
              const dotColor = overdue ? "#dc2626" : urgent ? "#d97706" : "#64748b";
              return (
                <div key={t.id} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "8px 14px", background: rowColor,
                  borderBottom: "1px solid #f1f5f9",
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: dotColor, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 13, color: "#1e293b" }}>{t.description}</span>
                    {t.clientName && <span style={{ fontSize: 11, color: "#64748b", marginRight: 6 }}>· {t.clientName}</span>}
                  </div>
                  {t.dueDate && (
                    <span style={{ fontSize: 10, color: dotColor, flexShrink: 0, fontWeight: 600 }}>
                      {overdue ? `באיחור ${Math.abs(diff)}י׳` : diff === 0 ? "היום" : diff === 1 ? "מחר" : fmtDate(t.dueDate)}
                    </span>
                  )}
                  <button
                    onClick={() => onUpdate(t.id, { done: true })}
                    title="סמן כבוצע"
                    style={{ ...iconBtn, fontSize: 15, color: "#16a34a" }}
                  >✓</button>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Filters ── */}
        <div style={{
          padding: "10px 16px 0", display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap",
        }}>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ ...inputStyle, fontSize: 11, flex: "0 0 auto" }}>
            <option value="הכל">כל הסוגים</option>
            <option value="פתוחות">🔔 משימות פתוחות</option>
            {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
          </select>
          <input
            list="log-client-options"
            value={filterClient}
            onChange={e => setFilterClient(e.target.value)}
            placeholder="🔍 חיפוש לקוח..."
            style={{ ...inputStyle, fontSize: 11, flex: 1, minWidth: 120 }}
          />
          <datalist id="log-client-options">
            {clientOptions.map(n => <option key={n} value={n} />)}
          </datalist>
          {(filterType !== "הכל" || clientQuery) && (
            <button onClick={() => { setFilterType("הכל"); setFilterClient(""); }}
              style={{ fontSize: 11, background: "transparent", border: "none", cursor: "pointer", color: "#94a3b8" }}>
              ✕ נקה
            </button>
          )}
        </div>

        {/* ── Add button / form ── */}
        <div style={{
          margin: "10px 16px 0", background: "white",
          borderRadius: 10, border: "1px solid #e2e8f0",
          overflow: "hidden", flexShrink: 0,
        }}>
          {!showForm ? (
            <button
              onClick={() => setShowForm(true)}
              style={{
                width: "100%", padding: "11px 0",
                background: "white", color: "#4338ca",
                border: "none", cursor: "pointer",
                fontWeight: 700, fontSize: 13,
              }}
            >+ הוסף פעולה</button>
          ) : (
            <form onSubmit={handleSubmit} style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>

              {/* Client */}
              <label style={labelStyle}>
                <span style={labelText}>לקוח</span>
                <input
                  list="log-client-options"
                  value={form.clientName}
                  onChange={e => setForm(f => ({ ...f, clientName: e.target.value }))}
                  placeholder="🔍 הקלד שם לקוח..."
                  style={inputStyle}
                />
              </label>

              {/* Type + Date */}
              <div style={{ display: "flex", gap: 10 }}>
                <label style={{ ...labelStyle, flex: 1 }}>
                  <span style={labelText}>סוג</span>
                  <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} style={inputStyle}>
                    {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                  </select>
                </label>
                <label style={{ ...labelStyle, flex: "0 0 130px" }}>
                  <span style={labelText}>{form.type === "task" ? "תאריך יעד" : "תאריך"}</span>
                  <input
                    type="date"
                    value={form.type === "task" ? form.dueDate : form.date}
                    onChange={e => setForm(f => form.type === "task"
                      ? { ...f, dueDate: e.target.value }
                      : { ...f, date: e.target.value }
                    )}
                    style={inputStyle}
                  />
                </label>
              </div>

              {/* Fee extras */}
              {form.type === "fee" && (
                <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                  <label style={{ ...labelStyle, flex: 1 }}>
                    <span style={labelText}>סכום שסוכם ₪</span>
                    <input type="number" placeholder="0" min={0} value={form.amount}
                      onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} style={inputStyle} />
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, paddingBottom: 8, cursor: "pointer" }}>
                    <input type="checkbox" checked={form.feeCollected}
                      onChange={e => setForm(f => ({ ...f, feeCollected: e.target.checked }))} />
                    <span style={{ fontSize: 12, color: "#475569" }}>שולם</span>
                  </label>
                </div>
              )}

              {/* Note reminder */}
              {form.type === "note" && (
                <label style={{ ...labelStyle, maxWidth: 160 }}>
                  <span style={labelText}>🔔 תזכורת (אופציונלי)</span>
                  <input type="date" value={form.reminder}
                    onChange={e => setForm(f => ({ ...f, reminder: e.target.value }))} style={inputStyle} />
                </label>
              )}

              <label style={labelStyle}>
                <span style={labelText}>תיאור</span>
                <textarea placeholder="תיאור הפעולה..." required autoFocus
                  value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  style={{ ...inputStyle, flex: "0 0 auto", resize: "vertical", minHeight: "60px", fontFamily: "inherit" }} />
              </label>

              <div style={{ display: "flex", gap: 8 }}>
                <button type="submit" style={{
                  flex: 1, padding: "9px 0", background: "#4338ca", color: "white",
                  border: "none", borderRadius: 7, cursor: "pointer", fontWeight: 700, fontSize: 13,
                }}>שמור</button>
                <button type="button" onClick={() => setShowForm(false)} style={{
                  flex: "0 0 72px", padding: "9px 0", background: "white", color: "#64748b",
                  border: "1px solid #e2e8f0", borderRadius: 7, cursor: "pointer", fontSize: 13,
                }}>ביטול</button>
              </div>
            </form>
          )}
        </div>

        {/* ── Timeline ── */}
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px 20px" }}>
          {filtered.length === 0 && (
            <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, marginTop: 40 }}>אין רשומות</div>
          )}
          {filtered.map(entry => {
            const meta = TYPE_META[entry.type] || TYPE_META.meeting;
            const isDoneTask = entry.type === "task" && entry.done;
            const isEditing = editId === entry.id;

            return (
              <div key={entry.id} style={{
                display: "flex", gap: 10, alignItems: "flex-start",
                padding: "10px 12px", marginBottom: 6,
                background: "white", borderRadius: 8,
                border: `1px solid ${isEditing ? "#c7d2fe" : "#f1f5f9"}`,
              }}>
                {/* Icon */}
                <div style={{
                  width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                  background: meta.color + "18",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 15,
                }}>{meta.icon}</div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: meta.color }}>{meta.label}</span>
                    <div style={{ display: "flex", gap: 5, alignItems: "center", flexShrink: 0 }}>
                      {entry.type === "fee" && (
                        entry.feeCollected
                          ? <span style={badge("#dcfce7", "#166534")}>✅ שולם</span>
                          : <button onClick={() => onUpdate(entry.id, { feeCollected: true })}
                              style={badge("#fef3c7", "#92400e", true)} title="סמן כשולם">⏳ ממתין</button>
                      )}
                      {entry.type === "task" && isDoneTask && (
                        <span style={badge("#f1f5f9", "#64748b")}>✓ בוצע</span>
                      )}
                      {!isEditing && <span style={{ fontSize: 10, color: "#94a3b8" }}>{fmtDate(entry.date)}</span>}
                    </div>
                  </div>

                  {isEditing ? (
                    /* ── Edit mode ── */
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
                      <textarea
                        value={editForm.description}
                        onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                        style={{ ...inputStyle, flex: "0 0 auto", resize: "vertical", minHeight: 50, fontFamily: "inherit" }}
                        autoFocus
                      />
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <label style={{ ...labelStyle, flex: 1, minWidth: 120 }}>
                          <span style={labelText}>לקוח</span>
                          <input list="log-client-options" value={editForm.clientName}
                            onChange={e => setEditForm(f => ({ ...f, clientName: e.target.value }))}
                            style={{ ...inputStyle, fontSize: 12 }} />
                        </label>
                        <label style={{ ...labelStyle, flex: "0 0 125px" }}>
                          <span style={labelText}>תאריך</span>
                          <input type="date" value={editForm.date}
                            onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))}
                            style={{ ...inputStyle, fontSize: 12 }} />
                        </label>
                        {entry.type === "task" && (
                          <label style={{ ...labelStyle, flex: "0 0 125px" }}>
                            <span style={labelText}>תאריך יעד</span>
                            <input type="date" value={editForm.dueDate}
                              onChange={e => setEditForm(f => ({ ...f, dueDate: e.target.value }))}
                              style={{ ...inputStyle, fontSize: 12 }} />
                          </label>
                        )}
                        {entry.type === "fee" && (
                          <label style={{ ...labelStyle, flex: "0 0 100px" }}>
                            <span style={labelText}>סכום ₪</span>
                            <input type="number" min={0} value={editForm.amount}
                              onChange={e => setEditForm(f => ({ ...f, amount: e.target.value }))}
                              style={{ ...inputStyle, fontSize: 12 }} />
                          </label>
                        )}
                        {entry.type === "note" && (
                          <label style={{ ...labelStyle, flex: "0 0 125px" }}>
                            <span style={labelText}>🔔 תזכורת</span>
                            <input type="date" value={editForm.reminder}
                              onChange={e => setEditForm(f => ({ ...f, reminder: e.target.value }))}
                              style={{ ...inputStyle, fontSize: 12 }} />
                          </label>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => saveEdit(entry)} style={{
                          padding: "6px 16px", background: "#16a34a", color: "white",
                          border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 700, fontSize: 12,
                        }}>✓ שמור</button>
                        <button onClick={cancelEdit} style={{
                          padding: "6px 16px", background: "white", color: "#64748b",
                          border: "1px solid #e2e8f0", borderRadius: 6, cursor: "pointer", fontSize: 12,
                        }}>ביטול</button>
                      </div>
                    </div>
                  ) : (
                    /* ── Display mode ── */
                    <>
                      <div style={{
                        fontSize: 13, marginTop: 3,
                        textDecoration: isDoneTask ? "line-through" : "none",
                        color: isDoneTask ? "#94a3b8" : "#1e293b",
                        whiteSpace: "pre-wrap",
                      }}>{entry.description}</div>

                      <div style={{ display: "flex", gap: 8, marginTop: 3, flexWrap: "wrap" }}>
                        {entry.clientName && (
                          <span style={{ fontSize: 11, color: "#64748b" }}>👤 {entry.clientName}</span>
                        )}
                        {entry.amount > 0 && (
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#d97706" }}>
                            {Number(entry.amount).toLocaleString("he-IL")} ₪
                          </span>
                        )}
                        {entry.type === "task" && entry.dueDate && !entry.done && (() => {
                          const diff = dateDiffDays(entry.dueDate);
                          const color = diff < 0 ? "#dc2626" : diff <= 1 ? "#d97706" : "#64748b";
                          return (
                            <span style={{ fontSize: 11, color, fontWeight: 600 }}>
                              🗓 {diff < 0 ? `באיחור ${Math.abs(diff)}י׳` : diff === 0 ? "היום" : diff === 1 ? "מחר" : fmtDate(entry.dueDate)}
                            </span>
                          );
                        })()}
                        {entry.type === "signature" && <SignatureTracker entry={entry} />}
                        {entry.type === "note" && entry.reminder && (() => {
                          const diff = dateDiffDays(entry.reminder);
                          const due = diff !== null && diff <= 0;
                          return (
                            <span style={{ fontSize: 11, color: due ? "#dc2626" : "#64748b", fontWeight: due ? 700 : 400 }}>
                              🔔 {fmtDate(entry.reminder)}
                            </span>
                          );
                        })()}
                      </div>
                    </>
                  )}
                </div>

                {/* Actions */}
                {!isEditing && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 }}>
                    {entry.type === "task" && !entry.done && (
                      <button onClick={() => onUpdate(entry.id, { done: true })}
                        title="סמן כבוצע"
                        style={{ ...iconBtn, color: "#16a34a", fontSize: 14 }}>✓</button>
                    )}
                    <button onClick={() => startEdit(entry)}
                      style={{ ...iconBtn, color: "#3b82f6", fontSize: 13 }} title="ערוך">✏️</button>
                    <button onClick={() => onDelete(entry.id)}
                      style={{ ...iconBtn, color: "#cbd5e1" }} title="מחק">✕</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────
const iconBtn = {
  background: "transparent", border: "none", cursor: "pointer",
  fontSize: 16, color: "#94a3b8", padding: "4px",
  display: "flex", alignItems: "center", justifyContent: "center",
  borderRadius: 4,
};

const labelStyle = { display: "flex", flexDirection: "column", gap: 4 };
const labelText = { fontSize: 11, fontWeight: 700, color: "#64748b", textAlign: "right" };

const inputStyle = {
  flex: 1, fontSize: 13, padding: "8px 10px",
  border: "1px solid #e2e8f0", borderRadius: 6,
  outline: "none", direction: "rtl", textAlign: "right",
  background: "white", width: "100%", boxSizing: "border-box",
};

function badge(bg, color, clickable = false) {
  return {
    fontSize: 10, fontWeight: 700, padding: "2px 6px",
    background: bg, color, borderRadius: 4,
    border: "none", cursor: clickable ? "pointer" : "default",
  };
}
